import { useCallback, useEffect, useState } from "react";
import type { VehicleSocket } from "../../../../store/admin/vehiclesSocketStore";
import { useVehiclesSocketStore } from "../../../../store/admin/vehiclesSocketStore";
import VehicleCard from "./VehicleCard";
import VehicleRow from "./VehicleRow";
import { DENSITY_STORAGE_KEY, parseDensity, type ListDensity } from "./compactRow";
import { REVEAL_UNIT_EVENT, pendingReveal } from "../../../../features/unitNotifications/revealUnit";

interface VehicleListProps {
  vehicles: VehicleSocket[];
  density: ListDensity;
  isDarkMode: boolean;
  themeClasses: {
    cardBg: string;
    cardBorder: string;
    text: string;
    textSecondary: string;
    buttonHover: string;
  };
  selectedStatus: string | null;
  onFocus?: () => void;
}

// Preferencia por navegador: es una comodidad de quien mira, no un dato de la
// cuenta. localStorage puede no estar (ventana privada, sitio bloqueado): en
// ese caso se queda en compacta y no se recuerda, sin romper el panel.
export function useListDensity(): [ListDensity, (d: ListDensity) => void] {
  const [density, setDensity] = useState<ListDensity>(() => {
    try {
      return parseDensity(localStorage.getItem(DENSITY_STORAGE_KEY));
    } catch {
      return "compact";
    }
  });
  const update = useCallback((d: ListDensity) => {
    setDensity(d);
    try {
      localStorage.setItem(DENSITY_STORAGE_KEY, d);
    } catch {
      /* sin almacenamiento: vale sólo para esta sesión */
    }
  }, []);
  return [density, update];
}

// La lista de unidades del panel, compartida por escritorio y móvil.
// - "detailed": la tarjeta de siempre, una por unidad.
// - "compact": una fila por unidad; tocarla sigue la unidad en el mapa y
//   despliega SU tarjeta completa debajo (una a la vez), así no se pierde
//   ninguna acción de la tarjeta.
export default function VehicleList({
  vehicles,
  density,
  isDarkMode,
  themeClasses,
  selectedStatus,
  onFocus,
}: VehicleListProps) {
  const setFocusedVehicleId = useVehiclesSocketStore((s) => s.setFocusedVehicleId);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Una notificación pidió «Ver en el mapa» (ver revealUnit): misma selección que el clic en la fila.
  useEffect(() => {
    const reveal = (id: string | null) => {
      if (!id) return;
      setExpandedId(id);
      setFocusedVehicleId(id);
    };
    reveal(pendingReveal());
    const onReveal = (e: Event) => reveal((e as CustomEvent<string>).detail);
    window.addEventListener(REVEAL_UNIT_EVENT, onReveal);
    return () => window.removeEventListener(REVEAL_UNIT_EVENT, onReveal);
  }, [setFocusedVehicleId]);

  const toggle = useCallback(
    (id: string) => {
      if (expandedId === id) {
        setExpandedId(null);
        return;
      }
      setExpandedId(id);
      setFocusedVehicleId(id);
    },
    [expandedId, setFocusedVehicleId],
  );

  const card = (vehicle: VehicleSocket) => (
    <VehicleCard
      key={vehicle.id}
      vehicle={vehicle}
      isDarkMode={isDarkMode}
      themeClasses={themeClasses}
      selectedStatus={selectedStatus}
      onFocus={onFocus}
    />
  );

  if (density === "detailed") return <>{vehicles.map(card)}</>;

  return (
    <>
      {vehicles.map((vehicle) => (
        <div key={vehicle.id}>
          <VehicleRow
            vehicle={vehicle}
            isExpanded={expandedId === vehicle.id}
            onToggle={toggle}
            isDarkMode={isDarkMode}
            themeClasses={themeClasses}
          />
          {expandedId === vehicle.id && <div className="mt-1 mb-2">{card(vehicle)}</div>}
        </div>
      ))}
    </>
  );
}
